import { useMemo, useState } from 'react';

import { PlaylistAddCheck } from '@material-ui/icons';

import RequestServiceDocumentList from './request.service.documents.list.component';
import RequestAnalysisCustomerView from '../analysis/request.analysis.customer.view';
import RequestHearingAnalysisCustomerView from '../hearing/request.hearing.analysis.customer.view';
import RequestHearingCustomerView from '../hearing/request.hearing.customer.view';
import DocumentPreview from '../../solicitations/documents/document.preview.component';
import modalities from '../../../domain/constant/modalities.constant';
import requestCorrectionService from '../../../domain/services/request/request.correction.service';
import { categorizeServiceDocuments } from '../../../domain/services/request/service/request.service.document.categorizer';
import Button from "../../shared/Button";
import { Alert, AlertTitle } from "@material-ui/lab";

const RequestServiceDocumentsContent = ({
    documents,
    request,
    additionalDocuments,
}) => {

    const [preview, setPreview] = useState({ show: false });
    const [expandedServiceIds, setExpandedServiceIds] = useState({});

    const handleOpenPreview = (file) => setPreview({ ...file, show: true });

    const renderButton = ({ label, onClick }) => (
        <div>
            <Button
                type="primary"
                className="!rounded-lg !text-sm md:!text-base h-10 !font-normal"
                onClick={onClick}
            >
                {label}
            </Button>
        </div>
    );

    const specificContentByServiceKey = (item) => ({
        analysis: {
            name: item.name,
            icon: PlaylistAddCheck,
            action: () => (
                <RequestAnalysisCustomerView
                    requestId={item.id}
                    buttonVariant="contained"
                    buttonTitle="Acessar"
                    buttonWidth="100%"
                    CustomButton={({ onClick }) => renderButton({ onClick, label: 'Acessar' })}
                />
            )
        },
        hearingAnalysis: {
            name: item.name,
            icon: PlaylistAddCheck,
            action: () => (
                <RequestHearingAnalysisCustomerView
                    requestId={item.id}
                    buttonVariant="contained"
                    buttonTitle="Acessar"
                    buttonWidth="100%"
                    CustomButton={({ onClick }) => renderButton({ onClick, label: 'Acessar' })}
                />
            )
        },
        hearing: {
            name: item.name,
            icon: PlaylistAddCheck,
            action: () => (
                <RequestHearingCustomerView
                    requestId={item.id}
                    buttonVariant="contained"
                    buttonTitle="Acessar"
                    buttonWidth="100%"
                    CustomButton={({ onClick }) => renderButton({ onClick, label: 'Acessar' })}
                />
            )
        },
        hearingInPerson: {
            name: item.name,
            icon: PlaylistAddCheck,
            action: () => (
                <RequestHearingCustomerView
                    requestId={item.id}
                    buttonVariant="contained"
                    buttonTitle="Acessar"
                    buttonWidth="100%"
                    CustomButton={({ onClick }) => renderButton({ onClick, label: 'Acessar' })}
                />
            )
        }
    })[item.key];

    const allCurrentDocs = useMemo(
        () => (documents.documentsByService || []).flatMap(svc => svc.documents || []),
        [documents]
    );

    const hasDocInCorrection = allCurrentDocs.some(doc => doc.isUnderCorrection && doc.isCurrent);

    const slaLabel = requestCorrectionService.getCorrectionDueDateHoursText(request || {});

    const renderCorrectionBanner = () => {
        if (!hasDocInCorrection) return null;
        return (
            <Alert severity="info" className="!rounded-lg">
                <AlertTitle>Solicitação em correção</AlertTitle>
                O documento atual continua disponível abaixo. A nova versão será publicada em até {slaLabel}.
            </Alert>
        );
    };

    const toggleService = (serviceId) =>
        setExpandedServiceIds(prev => ({ ...prev, [serviceId]: !prev[serviceId] }));

    const renderServiceBlock = (item) => {

        const specificContent = specificContentByServiceKey(item);
        const { main, previousVersions, complementary } = categorizeServiceDocuments(item.documents || []);

        const isExpanded = !!expandedServiceIds[item.id];
        const hasPrevious = previousVersions.length > 0;
        const hasComplementary = complementary.length > 0;

        const primaryDocs = [];
        if (specificContent) primaryDocs.push(specificContent);
        primaryDocs.push(...main);

        const hasPrimary = primaryDocs.length > 0;
        const isEmpty = !hasPrimary && !hasComplementary;

        return (
            <div key={item.id} className="flex flex-col gap-3">
                <div className="flex items-baseline gap-2">
                    <span className="text-sm font-medium text-gray-700">{item.name}</span>
                </div>

                {isEmpty && (
                    <div className="rounded-lg bg-gray-50 px-4 py-3 text-sm text-gray-500">
                        Sua entrega aparecerá aqui assim que estiver pronta.
                    </div>
                )}

                {hasPrimary && (
                    <RequestServiceDocumentList
                        request={request}
                        documents={primaryDocs}
                        openPreview={handleOpenPreview}
                        serviceName={item.name}
                        isNotion={item.serviceKey == modalities.notion.key}
                        variant="primary"
                    />
                )}

                {hasComplementary && (
                    <div className="flex flex-col gap-2 mt-1">
                        <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
                            Anexos complementares
                        </span>
                        <RequestServiceDocumentList
                            request={request}
                            documents={complementary}
                            openPreview={handleOpenPreview}
                            serviceName={item.name}
                            isNotion={item.serviceKey == modalities.notion.key}
                            variant="secondary"
                            category="complementary"
                        />
                    </div>
                )}

                {hasPrevious && (
                    <div className="flex flex-col gap-2">
                        <button
                            type="button"
                            onClick={() => toggleService(item.id)}
                            className="self-start text-xs font-medium text-gray-500 hover:text-gray-700 underline-offset-2 hover:underline"
                        >
                            {isExpanded
                                ? 'Ocultar versões anteriores'
                                : `Ver versões anteriores (${previousVersions.length})`}
                        </button>

                        {isExpanded && (
                            <RequestServiceDocumentList
                                request={request}
                                documents={previousVersions}
                                openPreview={handleOpenPreview}
                                serviceName={item.name}
                                isNotion={item.serviceKey == modalities.notion.key}
                                variant="secondary"
                                category="previous-version"
                            />
                        )}
                    </div>
                )}
            </div>
        );
    };

    const filteredAdditional = (additionalDocuments || []).filter(doc => !doc.correctionSource);

    const hasAnyServiceContent = (documents.documentsByService || []).some(svc => {
        if (specificContentByServiceKey(svc)) return true;
        const cat = categorizeServiceDocuments(svc.documents || []);
        return cat.main.length > 0 || cat.complementary.length > 0;
    });
    const hasAdditional = filteredAdditional.length > 0;

    if (!hasAnyServiceContent && !hasAdditional) {
        return (
            <div className="rounded-lg bg-gray-50 px-4 py-6 text-sm text-gray-600 text-center">
                Sua entrega aparecerá aqui assim que estiver pronta.
            </div>
        );
    }

    return (
        <>
            <div className="flex flex-col gap-8">
                {renderCorrectionBanner()}

                {(documents.documentsByService || []).map(renderServiceBlock)}

                {hasAdditional && (
                    <div className="flex flex-col gap-3 pt-2 border-t border-gray-100">
                        <div className="flex items-baseline gap-2">
                            <span className="text-sm font-medium text-gray-700">Documentos do processo</span>
                        </div>

                        <RequestServiceDocumentList
                            request={request}
                            documents={filteredAdditional}
                            openPreview={handleOpenPreview}
                            variant="secondary"
                        />
                    </div>
                )}
            </div>

            {preview.show && (
                <DocumentPreview
                    file={preview}
                    onSelect={() => setPreview({ show: false })}
                />
            )}
        </>
    );
};

export default RequestServiceDocumentsContent;
